import { Box, Chip, FormControlLabel, Radio, Typography } from '@mui/material';

import { useField } from 'formik';

import {
    GeneralLanguageSpecificAssets,
    InterviewLanguage,
    MultipleChoiceLanguageSpecificAssets,
    PuppetQuestion,
    Question,
    QuestionType,
} from '@synergetx/puppet-interview-types';
import { InterviewEdited } from 'interviewEditor/types';
import invariant from 'tiny-invariant';
import { memo } from 'react';

import { htmlToPlainText } from './richText';

interface Props {
    question: Question;
}
const MAX_MULTI_CHOICE_PREVIEW_LENGTH = 4;

function SurveyQuestionPromptPreview({ question }: Props) {
    const [{ value: languages }, ,] = useField<InterviewEdited['languages']>('languages');

    const language = languages?.[0] || 'English';

    switch (question.type) {
        case QuestionType.MULTIPLE:
            // eslint-disable-next-line no-case-declarations
            const questionChoices = (
                question?.localeContent?.[language] as MultipleChoiceLanguageSpecificAssets
            )?.choices;
            return (
                <>
                    {questionChoices
                        ?.slice(0, MAX_MULTI_CHOICE_PREVIEW_LENGTH)
                        .map((choice, idx) => {
                            return (
                                <FormControlLabel
                                    key={idx}
                                    value={choice}
                                    control={<Radio id={choice} name={choice} disabled />}
                                    label={choice}
                                />
                            );
                        })}
                    {(questionChoices?.length || 0) > MAX_MULTI_CHOICE_PREVIEW_LENGTH ? (
                        <Chip
                            sx={{ margin: 0.25 }}
                            key={`overflow-${question.position}`}
                            label={`+${
                                (questionChoices?.length || MAX_MULTI_CHOICE_PREVIEW_LENGTH) -
                                MAX_MULTI_CHOICE_PREVIEW_LENGTH
                            } more...`}
                        />
                    ) : null}
                </>
            );
        case QuestionType.PUPPET:
            return <PuppetQuestionText language={language} question={question as PuppetQuestion} />;
        // OPEN, INFO, DATE, NUMBER and everything else all preview the same `content` string.
        // They used to be written out one branch at a time and only INFO stripped its markup, so a
        // continue prompt or an open question showed the reader raw `<p><span style=...>` tags.
        default: {
            const content = (
                question?.localeContent?.[language] as GeneralLanguageSpecificAssets | undefined
            )?.content;

            if (!content) return null;

            return (
                <Typography variant="body1" sx={{ color: 'darkgrey' }}>
                    {htmlToPlainText(content)}
                </Typography>
            );
        }
    }
}

interface PuppetQuestionTextProps {
    language: InterviewLanguage;
    question: PuppetQuestion;
}

function PuppetQuestionText({ language, question }: PuppetQuestionTextProps) {
    invariant(question && language, '[PuppetQuestionText]: missing info');

    if (question) {
        return (
            <Box
                sx={{
                    border: '0.01px solid rgba(0,0,0,.2)',
                    padding: '0.5rem',
                    borderRadius: '5px',
                    mt: '.5rem',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    backgroundColor: '#efefef',
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        color: '#333',
                    }}
                >
                    {question?.localeContent?.[language]?.leftText}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#333',
                    }}
                >
                    {question?.localeContent?.[language]?.rightText}
                </Typography>
            </Box>
        );
    }
    return null;
}

export default memo(SurveyQuestionPromptPreview);
